import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { useEnvStore } from "@/os/env/useEnvStore";
import { osKey } from "@/os/data/queries";
import { db } from "@/os/data";
import type { AppRole } from "@/auth/AuthProvider";
import { useOSThemeStore } from "@/os/theme/useOSTheme";
import scIconDark from "@/assets/sc-icon-dark.png";
import scIconLight from "@/assets/sc-icon-light.png";
import { Loader2 } from "lucide-react";

/**
 * Full-screen splash shown once per browser session when the user enters the
 * Smart Canvas OS. While visible we prefetch the role-appropriate data into
 * the React Query cache so subsequent navigation is instant.
 *
 * Behaviour:
 *  - Only shows on the first OS mount of the session (sessionStorage flag).
 *  - Crossfades through 4 short inspirational phrases.
 *  - Hides when the prefetch finishes AND a minimum display time has elapsed
 *    (so it never flashes), with a hard timeout as a safety net.
 */

const SESSION_KEY = "sc.os.booted";
const MIN_DURATION_MS = 900;
const MAX_DURATION_MS = 4000;

export function OSBootSplash({ role, userId }: { role: AppRole; userId: string | null }) {
  const env = useEnvStore((s) => s.env);
  const qc = useQueryClient();

  const shouldShow = useMemo(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(SESSION_KEY) !== "1";
  }, []);

  const [visible, setVisible] = useState(shouldShow);
  const [progress, setProgress] = useState(0);
  const startedAt = useRef<number>(Date.now());
  const osDark = useOSThemeStore((s) => s.theme) === "dark";
  const iconSrc = osDark ? scIconDark : scIconLight;

  // Prefetch role-appropriate data into the cache.
  useEffect(() => {
    if (!visible) return;
    const tasks: Promise<unknown>[] = [];
    const pf = (parts: readonly unknown[], fn: () => Promise<unknown>) =>
      tasks.push(qc.prefetchQuery({ queryKey: osKey(env, ...parts), queryFn: fn }));

    if (role === "admin") {
      pf(["projects"], () => db().listProjects());
      pf(["clients"], () => db().listClients());
      pf(["quotes"], () => db().listQuotes());
      pf(["invoices"], () => db().listInvoices());
      pf(["team_profiles"], () => db().listTeamProfiles());
      pf(["users_with_roles"], () => db().listUsersWithRoles());
      pf(["resources"], () => db().listResources());
    } else if (role === "operator" && userId) {
      pf(["operator_assigned_projects", userId], () => db().operatorAssignedProjects(userId));
      pf(["team_profiles"], () => db().listTeamProfiles());
    } else if (role === "client" && userId) {
      pf(["client_hub_data", userId], () => db().clientHubData(userId));
      pf(["client_invoices", userId], () => db().clientInvoices(userId));
    }

    const elapsed = () => Date.now() - startedAt.current;
    const finish = () => {
      const remain = Math.max(0, MIN_DURATION_MS - elapsed());
      window.setTimeout(() => {
        sessionStorage.setItem(SESSION_KEY, "1");
        setVisible(false);
      }, remain);
    };

    let safety = window.setTimeout(finish, MAX_DURATION_MS);
    Promise.allSettled(tasks).then(() => {
      window.clearTimeout(safety);
      finish();
    });
    return () => window.clearTimeout(safety);
  }, [visible, role, userId, env, qc]);

  // Drive a smooth progress value (eases toward 95%; jumps to 100% on finish).
  useEffect(() => {
    if (!visible) {
      setProgress(100);
      return;
    }
    let raf = 0;
    const tick = () => {
      setProgress((p) => p + (95 - p) * 0.04);
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [visible]);

  if (!shouldShow) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="os-boot-splash"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.25, 1, 0.5, 1] }}
          className="fixed inset-0 z-[100] bg-background text-foreground"
        >
          {/* Centered brand mark + spinner so the screen never looks blank. */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
            <img
              src={iconSrc}
              alt="Smart Canvas"
              className="h-12 w-12 rounded-md object-contain opacity-95"
            />
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              <span>Smart Canvas OS</span>
            </div>
          </div>
          {/* Full-width loading bar pinned to the bottom of the viewport. */}
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-1 overflow-hidden bg-foreground/15"
          >
            <motion.div
              className="h-full bg-foreground"
              initial={{ width: "0%" }}
              animate={{ width: `${progress}%` }}
              transition={{ ease: [0.25, 1, 0.5, 1], duration: 0.4 }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}